import NavLinks from "ui/dashboard/link-render";
import { PowerIcon } from "@heroicons/react/24/outline";
import { SvgLogo, SvgMyDealer, SvgCopy } from "ui/svgs";
import Cookies from "js-cookie";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "context/AuthContext";
import { useNavigate } from "react-router-dom";
import { SignalContext } from "context/SignalContext";
import { Dropdown, Popover } from "antd";
import { getCustomers, getDealerInfo } from "./service";
import UserTag from "./userTag";
import { showToast } from "ui/components/common";
import { USER_ROLE } from "lib/constants";
import { jwtDecode } from "jwt-decode";
import DataControllerManager from "lib/dataControler";
import { DataControlerContext } from "context/DataControlerContext";
import { copyToClipBoard } from "lib/utils";
import { parsedPhoneNumber } from "lib/utils";
import _ from "lodash";
import "./styles.scss";

export default function SideNav({ fromFallback = false }) {
  const { setAuthenticated, privileges, setPrivileges } =
    useContext(AuthContext);
  const { signal } = useContext(SignalContext);
  const navigate = useNavigate();
  const [userList, setUserList] = useState([]);
  const { setUser, user } = useContext(AuthContext);
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const [dealerInfo, setDealerInfo] = useState(null);
  const { getControllerManagerWithWaiter } = useContext(DataControlerContext);

  useEffect(() => {
    if (
      !privileges.includes("dealerIcon") ||
      (user.role === USER_ROLE.ProjectManager && !currentCustomer)
    ) {
      return;
    }

    getDealerInfo().then((data) => {
      if (data.status !== 200) {
        showToast(1, "Failed to fetch dealer info");
        return;
      }
      const dealerInfo = data?.data?.company;
      setDealerInfo({
        contactName: _.trim(dealerInfo.contactName) || "-",
        email: dealerInfo.email || "-",
        phone: dealerInfo.phone || "-",
        name: dealerInfo.name || "-",
      });
    });
  }, [privileges, currentCustomer]);
  useEffect(() => {
    if (fromFallback) return;
    if (currentCustomer && user.role === USER_ROLE.ProjectManager) {
      const user = userList?.find((item) => item.key === currentCustomer.orgId);
      Cookies.set("orgId", currentCustomer.orgId);
      //@ts-ignore
      setUser((state) => {
        state.orgId = currentCustomer.orgId;
        state.phoneNumber = user?.extraData.phoneNumber;
        state.email = user?.extraData.email;
        return { ...state };
      });
      navigate("/dashboard/home");

      DataControllerManager.orgId = currentCustomer.orgId;
      getControllerManagerWithWaiter().then((manager) => {
        manager?.clearDataControllers();
      });
    }
  }, [currentCustomer]);

  useEffect(() => {
    if (fromFallback) return;
    if (user.role !== USER_ROLE.ProjectManager) return;
    const token = Cookies.get("token");
    const tokenDecoded = jwtDecode<any>(token!);
    const lastOrgId = Cookies.get("orgId");
    getCustomers(tokenDecoded.org_id).then((data) => {
      if (data.status !== 200) {
        showToast(1, "Failed to fetch customers");
        return;
      }
      const _userList = data?.data?.data;

      setUserList(
        _userList?.map((item) => {
          return {
            label: (
              <UserTag
                label={item.customer.accountName}
                email={item.customer.user.email}
                orgId={item.customer.orgId}
                onSelect={(orgId, name) => {
                  setCurrentCustomer({ name, orgId });
                }}
              />
            ),
            key: item.customer.orgId,
            extraData: item.customer.user,
          };
        }),
      );
      // Keep the PM selected user when the page is refreshed
      const orgIndex = Math.max(
        _userList?.findIndex((user) => user.customer.orgId == lastOrgId),
        0,
      );

      Cookies.set("orgId", _userList[orgIndex]?.customer.orgId);
      setCurrentCustomer({
        name: _userList[orgIndex]?.customer.accountName ?? "",
        orgId: _userList[orgIndex]?.customer.orgId ?? "",
      });
    });
  }, []);

  function handleSignOut() {
    signal?.close?.();
    Cookies.remove("token", { domain: "." + window.location.hostname });
    setAuthenticated(false);
    setPrivileges([]);
    setTimeout(() => navigate("/login"));
  }

  const myDealerContent = (
    <div className="w-[320px] group">
      <p className="font-semibold text-xl text-[#222222]">My Dealer</p>
      <div className="w-full mt-1">
        <div className="w-full flex">
          <p className="text-[#717171] w-[130px]">Contact Name</p>
          <p className="text-[#222222] font-semibold">
            {dealerInfo?.contactName}
          </p>
        </div>
        <div className="w-full flex">
          <p className="text-[#717171] w-[130px]">Email</p>
          <p className="text-[#222222] font-semibold text-nowrap max-w-[180px] overflow-hidden break-words text-ellipsis">
            {dealerInfo?.email}
          </p>
          <button
            className=" hover:fill-[#939393] fill-[#717171] float-right invisible group-hover:visible"
            onClick={() => copyToClipBoard(dealerInfo?.email)}
          >
            <SvgCopy width="16" className="ml-1 " />
          </button>
        </div>
        <div className="w-full flex">
          <p className="text-[#717171] w-[130px]">Phone</p>
          <p className="text-[#222222] font-semibold">
            {parsedPhoneNumber(dealerInfo?.phone)}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-full flex-col px-3 py-4 md:px-6 text-zinc-950 bg-slate-50">
      <div className="flex grow flex-row space-x-2 md:flex-col md:space-x-0 md:space-y-2">
        <div className="flex justify-center">
          <button className="flex h-[48px] grow items-center justify-center gap-2 rounded-full p-3 text-sm font-medium hover:bg-zinc-100 hover:text-blue-600 md:flex-none md:justify-start md:p-2 md:px-3">
            <SvgLogo width="24" className="fill-neutral-800" withText={false} />
          </button>
        </div>
        <NavLinks />
        <div className="hidden h-auto w-full grow rounded-md md:block" />
        {privileges.includes("dealerIcon") && (
          <Popover
            content={myDealerContent}
            placement="right"
            trigger="click"
            title=""
            arrow={false}
            color="#eeeeee"
          >
            <div className="cursor-pointer flex h-[48px] grow items-center justify-center gap-3 rounded-full p-3 text-sm font-medium z-[100] md:flex-none md:justify-start md:p-2 md:px-3 hover:bg-zinc-100">
              <SvgMyDealer className="w-6" />
            </div>
          </Popover>
        )}

        {currentCustomer && user?.role === USER_ROLE.ProjectManager && (
          <Dropdown
            overlayClassName="max-h-[300px] overflow-y-auto"
            menu={{
              items: userList,
              selectable: true,
              selectedKeys: [currentCustomer.orgId.toString()],
            }}
            placement="topRight"
          >
            <div
              style={{
                cursor: "default",
                width: "24px",
                height: "24px",
                borderRadius: "80px",
                backgroundColor: "blue",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontSize: "14px",
                color: "white",
                alignSelf: "center",
                paddingBottom: "2px",
              }}
            >
              {currentCustomer?.name?.substr(0, 1)}
            </div>
          </Dropdown>
        )}

        <button
          className="group flex h-[48px] grow items-center justify-center gap-3 rounded-full rounded-2xl p-3 text-sm font-medium z-[100] hover:w-40 md:flex-none md:justify-start md:p-2 md:px-3 hover:bg-zinc-100"
          onClick={handleSignOut}
        >
          <PowerIcon className="w-6" />
          <div className="hidden group-hover:block">Sign Out</div>
        </button>
      </div>
    </div>
  );
}
